import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSidebar } from "./Sidebar";
import { getRoleBadgeColor } from "@/mocks/auth";
import { api } from "@/api/client";

type Notif = { id: number; title: string; time: string; unread: boolean; risk: string };

function timeAgo(ts: string): string {
  const diffMs = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Topbar() {
  const { setMobileOpen } = useSidebar();
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notif[]>([]);
  const [readIds, setReadIds] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const { theme, toggleTheme } = useTheme();
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation();

  const displayUser = user;

  useEffect(() => {
    if (!displayUser) return;
    api.alerts({ status: "active", per_page: 8 })
      .then((res: any) => {
        const items = res.items ?? res ?? [];
        setNotifications(
          items.map((a: any) => ({
            id: a.id,
            title: `${a.risk_level === "high" ? "High" : a.risk_level === "medium" ? "Medium" : "Low"}-risk alert: ${a.district_name ?? `District ${a.district_id}`}`,
            time: timeAgo(a.created_at),
            unread: true,
            risk: a.risk_level ?? "low",
          }))
        );
      })
      .catch(() => {});
  }, [!!displayUser]);

  if (!displayUser) return null;

  const unreadCount = notifications.filter((n) => !readIds.has(n.id)).length;

  function markAllRead() {
    setReadIds(new Set(notifications.map((n) => n.id)));
  }

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    navigate("/login");
  };

  const languages = [
    { code: "en" as const, label: "English", flag: "GB" },
    { code: "fr" as const, label: "French", flag: "FR" },
  ];

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between px-3 md:px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md">
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(true)}
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <i className="ri-menu-line text-lg" />
        </button>

        {/* Breadcrumb / Context */}
        <div className="hidden md:flex items-center gap-2">
          <span className="text-xs text-slate-400 dark:text-slate-500">EpiSense Platform</span>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <span className="text-xs text-slate-600 dark:text-slate-300">{displayUser.organizationName}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Role Badge (desktop) */}
        <span className={`hidden md:inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getRoleBadgeColor(displayUser.role)}`}>
          {displayUser.role}
        </span>

        {/* Org Badge */}
        <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald" />
          {displayUser.organizationName}
        </span>

        {/* Language Toggle */}
        <div className="relative">
          <button
            onClick={() => { setLangOpen(!langOpen); setNotifOpen(false); setProfileOpen(false); }}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title={t("lang_switch")}
          >
            <span className="text-xs font-bold font-mono uppercase">{lang === "en" ? "EN" : "FR"}</span>
          </button>
          {langOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
              <div className="absolute right-0 top-11 w-40 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl z-50 overflow-hidden">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm w-full text-left transition-colors ${
                      lang === l.code
                        ? "bg-teal-muted/20 text-teal"
                        : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold border border-slate-300 dark:border-slate-600">
                      {l.flag}
                    </span>
                    {l.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title={theme === "dark" ? t("common_light_mode") : t("common_dark_mode")}
        >
          <i className={theme === "dark" ? "ri-sun-line text-lg" : "ri-moon-line text-lg"} />
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              setNotifOpen(!notifOpen);
              setProfileOpen(false);
              setLangOpen(false);
            }}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors relative"
          >
            <i className="ri-notification-3-line text-lg" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div className="absolute right-0 top-11 w-80 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{t("nav_alerts")}</h4>
                    {notifications.length > 0 && <p className="text-[10px] text-slate-400">{unreadCount} unread</p>}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-slate-400 dark:text-slate-500 hover:text-teal transition-colors text-xs"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-xs text-slate-400 dark:text-slate-600 text-center py-8">No active alerts</p>
                  ) : (
                    notifications.map((n) => {
                      const isRead = readIds.has(n.id);
                      const riskDot: Record<string, string> = { high: "bg-red-500", medium: "bg-amber-400", low: "bg-emerald-500" };
                      return (
                        <div
                          key={n.id}
                          onClick={() => { setReadIds((prev) => new Set([...prev, n.id])); navigate("/alerts"); setNotifOpen(false); }}
                          className={`px-4 py-3 border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer ${
                            !isRead ? "bg-slate-50 dark:bg-slate-800/20" : ""
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${riskDot[n.risk] ?? "bg-slate-400"}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700 dark:text-slate-200 leading-snug">{n.title}</p>
                              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{n.time}</p>
                            </div>
                            {!isRead && <span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0 mt-1.5" />}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => { setNotifOpen(false); navigate("/alerts"); }}
                    className="text-xs text-teal hover:text-teal-hover transition-colors w-full text-center py-1"
                  >
                    {t("dash_view_all")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* User */}
        <div className="relative">
          <button
            onClick={() => {
              setProfileOpen(!profileOpen);
              setNotifOpen(false);
              setLangOpen(false);
            }}
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-muted/30 text-teal text-xs font-bold">
              {displayUser.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-xs font-medium text-slate-900 dark:text-white leading-tight">{displayUser.name}</p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-tight">{displayUser.email}</p>
            </div>
            <i className="ri-arrow-down-s-line text-xs text-slate-400 dark:text-slate-500 hidden md:block" />
          </button>
          {profileOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
              <div className="absolute right-0 top-11 w-56 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{displayUser.name}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">{displayUser.email}</p>
                  <span className={`inline-flex items-center mt-1.5 px-2 py-0.5 rounded text-[10px] font-medium border ${getRoleBadgeColor(displayUser.role)}`}>
                    {displayUser.role}
                  </span>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { setProfileOpen(false); navigate("/settings"); }}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 w-full text-left transition-colors"
                  >
                    <i className="ri-settings-3-line text-xs" />
                    {t("nav_settings")}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 w-full text-left transition-colors"
                  >
                    <i className="ri-logout-box-line text-xs" />
                    {t("nav_logout")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}